<template>
  <popup-dialog
    :visible="visible"
    :close-button-visible="true"
    :resizable="true"
    :width="Math.min(windowWidth - 80, 1200)"
    header-class="expression-editor__header"
    class="expression-editor"
    @show="onShow()"
    @close="onClose()"
  >
    <template #header>
      <div class="expression-editor__headline">
        <div>
          <template v-if="affectedItem">
            <div>{{ $t(`objectNames.${affectedItem.type}`) }}</div>
            <h3 class="mv-0 expression-editor__heading">
              {{ affectedItemTitle }}
            </h3>
          </template>
          <template v-else>
            <h3 class="mv-0 expression-editor__heading">
              {{ $t("headings.expressionEditor") }}
            </h3>
          </template>
        </div>
        <input-state-toggle
          v-slot="{ state }"
          :value="inputType"
          :states="['template', 'expression', 'tree']"
          @update:value="setInputType($event)"
        >
          <span style="font-size: 1rem" :title="$t(`expressionInputTypes.${state}`)">
            <template v-if="state == 'expression'">
              <lucide-icon name="function-square" />
            </template>
            <template v-else-if="state == 'tree'">
              <lucide-icon name="network" />
            </template>
            <template v-else>
              <lucide-icon name="layout-panel-top" />
            </template>
          </span>
        </input-state-toggle>
      </div>
    </template>

    <div v-if="inputType == 'expression'" class="expression-editor__expression">
      <expression-input
        v-if="visible"
        ref="expressionInputField"
        :tree="tree"
        :expression="expression"
        :parse-context="parseContext"
        :editor="'none'"
        @update:expression="updateExpression"
      />
    </div>
    <div v-else-if="inputType == 'tree'" class="expression-editor__tree">
      <expression-tree-view
        class="expression-editor__tree-view"
        :tree="tree"
        :editable="true"
        @update:expression="updateExpression"
      />
    </div>
    <div
      v-else-if="inputType == 'template'"
      class="expression-editor__template"
    >
      <span class="expression-editor__stepnumber">1</span>
      <expression-template-input
        ref="templateInput"
        :expression="expression"
        :tree="tree"
        :expression-parse-context="parseContext"
        @update:expression="updateExpression"
      />

      <div class="pl-4" v-if="expression.length > 1">
        {{ $t("labels.result") }}
        <div class="expression-editor__result mt-2 pl-0">
          =
          <span class="expression-editor__result-value">
            {{ formatNumber(evaluatedExpression?.value)
            }}{{ evaluatedExpression?.unit }}
          </span>
          <span class="expression-editor__result-range">
            ({{ $t("labels.min") }}: {{ formatNumber(evaluatedExpression?.minValue) }}, {{ $t("labels.max") }}:
            {{ formatNumber(evaluatedExpression?.maxValue) }})
          </span>
        </div>
      </div>
    </div>

    <div v-if="inputType == 'expression'" class="expression-editor__result">
      =
      <span class="expression-editor__result-value">
        {{ formatNumber(evaluatedExpression?.value)
        }}{{ evaluatedExpression?.unit }}
      </span>
      <span class="expression-editor__result-range">
        ({{ $t("labels.min") }}: {{ formatNumber(evaluatedExpression?.minValue) }}, {{ $t("labels.max") }}:
        {{ formatNumber(evaluatedExpression?.maxValue) }})
      </span>
    </div>

    <div class="line items-start p-4 gap-4">
      <span v-if="inputType == 'template'" class="expression-editor__stepnumber">2</span>
      <div class="expression-editor__controls" :class="{ '-with-step': inputType == 'template' }">
        <template v-if="inputType != 'template'">
          <h3 class="expression-editor__subtitle">
            <span class="expression-editor__prefix">#</span
            >{{ $t("headings.functions") }}
          </h3>
          <div class="expression-editor__buttonsarea">
            <input-text v-model:value="functionSearchText" />
            <div class="expression-editor__buttons">
              <button
                v-for="functionEntry in filteredFunctions"
                :key="functionEntry.code"
                type="button"
                class="button-pill"
                @click="insertFunction(functionEntry.code)"
              >
                {{ functionEntry.title }}
              </button>
            </div>
          </div>
  
          <h3 class="expression-editor__subtitle">
            <span class="expression-editor__prefix">+</span
            >{{ $t("headings.operators") }}
          </h3>
          <div class="expression-editor__buttonsarea">
            <div class="expression-editor__buttons">
              <button
                v-for="operatorEntry in baseOperators"
                :key="operatorEntry.code"
                type="button"
                class="button-pill expression-editor__operatorbutton"
                @click="insertOperator(operatorEntry.code)"
              >
                {{ operatorEntry.code }}
              </button>
  
              <template v-if="inputType == 'expression'">
                <button
                  v-for="parenthesis in parentheses"
                  :key="parenthesis.code"
                  type="button"
                  class="button-pill expression-editor__operatorbutton"
                  @click="insertParenthesis(parenthesis.code)"
                >
                  {{ parenthesis.title }}
                </button>
              </template>
            </div>
  
            <div class="expression-editor__buttons">
              <button
                v-for="operatorEntry in comparisonOperators"
                :key="operatorEntry.code"
                type="button"
                class="button-pill expression-editor__operatorbutton"
                @click="insertOperator(operatorEntry.code)"
              >
                {{ operatorEntry.code }}
              </button>
            </div>
          </div>
        </template>
  
        <h3 class="expression-editor__subtitle">
          <span class="expression-editor__prefix">@</span
          >{{ $t("objectNames.element", 4) }}
        </h3>
        <div class="expression-editor__buttonsarea">
          <input-text v-model:value="elementsSearchText" />
          <div v-if="filteredTargets.length > 0">
            <h4 class="label mt-0">{{ $t("objectNames.target", 4) }}</h4>
            <div class="expression-editor__buttons">
              <button
                v-if="inputType == 'template'"
                type="button"
                class="button-pill"
                @click="addTemplateGroupArg('all_targets')"
              >
                {{ $t("labels.allTargets") }}
              </button>
              <button
                v-for="target in filteredTargets"
                :key="target.uid"
                type="button"
                class="button-pill"
                @click="insertElement(target.uid)"
              >
                <template v-if="target.title">{{ target.title }}</template>
                <i v-else>{{ $t("labels.unnamed") }}</i>
              </button>
            </div>
          </div>
          <div v-if="filteredResources.length > 0">
            <h4 class="label mt-0">{{ $t("objectNames.resource", 4) }}</h4>
            <div class="expression-editor__buttons">
              <button
                v-if="inputType == 'template'"
                type="button"
                class="button-pill"
                @click="addTemplateGroupArg('all_resources')"
              >
                {{ $t("labels.allResources") }}
              </button>
              <button
                v-for="element in filteredResources"
                :key="element.uid"
                type="button"
                class="button-pill"
                @click="insertElement(element.uid)"
              >
                <template v-if="element.title">{{ element.title }}</template>
                <i v-else>{{ $t("labels.unnamed") }}</i>
              </button>
            </div>
          </div>
          <div v-if="filteredIndices.length > 0">
            <h4 class="label mt-0">{{ $t("objectNames.index", 4) }}</h4>
            <div class="expression-editor__buttons">
              <button
                v-for="element in filteredIndices"
                :key="element.uid"
                type="button"
                class="button-pill"
                @click="insertElement(element.uid)"
              >
                <template v-if="element.title">{{ element.title }}</template>
                <i v-else>{{ $t("labels.unnamed") }}</i>
              </button>
            </div>
          </div>
        </div>
  
        <h3 class="expression-editor__subtitle">
          <span class="expression-editor__prefix">$</span
          >{{ $t("headings.variables") }}
        </h3>
        <div class="expression-editor__buttonsarea">
          <input-text v-model:value="variablesSearchText" />
          <div class="expression-editor__buttons">
            <button
              v-for="variable in filteredVariables"
              :key="variable.code"
              :disabled="variable.available === false"
              type="button"
              class="button-pill"
              @click="insertVariable(variable.code)"
            >
              {{ variable.title }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useWindowSize } from "@vueuse/core";
import { formatNumber } from "@/helpers/utility/Formatting";

import InputText from "@core/input/InputText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import PopupDialog from "@core/ui/PopupDialog.vue";
import ExpressionInput from "./ExpressionInput.vue";
import ExpressionTreeView from "./ExpressionTreeView.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";
import ExpressionTemplateInput from "./ExpressionTemplateInput.vue";

import { useExpressionMenu } from "./useExpressionMenu";
import { useModelStore } from "../model/model.store";
import { useFlowStore } from "../flow/flows.store";

import type { ExpressionParseContext } from "../effect/Expression";
import type { ExpressionTree } from "./ExpressionTree";
import type { ExpressionValue, FunctionName } from "./expressionFunctions";
import type { ExpressionOperator } from "./ExpressionInputTypes";
import type {
  TemplateSingleArg,
  TemplateGroupArg
} from "./expressionTemplates";

type ExpressionEditorView = "expression" | "tree" | "template";

const props = withDefaults(defineProps<{
  visible: boolean;
  tree: ExpressionTree;
  expression: string;
  parseContext: ExpressionParseContext;
  inputMode?: ExpressionEditorView;
}>(), {
  inputMode: "expression"
});

const emit = defineEmits<{
  (event: "close"): void;
  (event: "update:expression", expression: string): void;
  (event: "update:input-mode", mode: ExpressionEditorView): void;
}>();

const { t } = useI18n();
const { width: windowWidth } = useWindowSize();

const templateInput = ref<InstanceType<typeof ExpressionTemplateInput>>();

const inputType = ref<ExpressionEditorView>(props.inputMode);
watch(() => props.inputMode, (type) => {
  inputType.value = type;

  if (type == "tree") {
    props.tree.setInsertMode("coarse");
  } else if (type == "expression") {
    props.tree.setInsertMode("fine");
  }
}, { immediate: true });
function setInputType(type: ExpressionEditorView) {
  inputType.value = type;

  if (type == "tree") {
    props.tree.setInsertMode("coarse");
  } else if (type == "expression") {
    props.tree.setInsertMode("fine");
  }

  emit("update:input-mode", type);
}

const affectedItem = computed(() => {
  if (
    props.parseContext.affectedType == null ||
    props.parseContext.affectedId == null
  )
    return null;
  return useModelStore().getItem(
    props.parseContext.affectedType,
    props.parseContext.affectedId
  );
});

const {
  targets,
  resources,
  indices,
  variables,
  functions,
  parentheses,
  baseOperators,
  comparisonOperators
} = useExpressionMenu();

const evaluatedExpression = computed((): ExpressionValue => {
  try {
    return props.tree.evaluateNode(props.tree.state.root);
  } catch (error) {
    return {
      value: NaN,
      minValue: NaN,
      maxValue: NaN,
      unit: "",
      unitValid: true,
      valid: false
    };
  }
});


function updateExpression(expression: string) {
  if (!props.visible) return;
  emit("update:expression", expression);
}

const functionSearchText = ref("");
const filteredFunctions = computed(() => {
  if (!functionSearchText.value) return functions.value;
  return functions.value.filter((f) => {
    const searchText = functionSearchText.value.toLowerCase();
    return (
      f.title.toLowerCase().includes(searchText) ||
      f.code.toLowerCase().includes(searchText)
    );
  });
});

const variablesSearchText = ref("");
const filteredVariables = computed(() => {
  return variables.value.filter((variable) => {
    if (variable.available === false) return false;
    const searchText = variablesSearchText.value.toLowerCase();
    if (!searchText) return true;
    return (
      variable.title.toLowerCase().includes(searchText) ||
      variable.code.toLowerCase().includes(searchText)
    );
  });
});

const elementsSearchText = ref("");
const filteredTargets = computed(() => {
  if (!elementsSearchText.value) return targets.value;
  return targets.value.filter((target) => {
    const searchText = elementsSearchText.value.toLowerCase();
    return target.title.toLowerCase().includes(searchText);
  });
});
const filteredResources = computed(() => {
  if (!elementsSearchText.value) return resources.value;
  return resources.value.filter((resource) => {
    const searchText = elementsSearchText.value.toLowerCase();
    return resource.title.toLowerCase().includes(searchText);
  });
});
const filteredIndices = computed(() => {
  const indicesWithoutAffected = indices.value.filter(
    (index) => index.uid != affectedItem.value?.uid
  );
  if (!elementsSearchText.value) return indicesWithoutAffected;
  return indicesWithoutAffected.filter((index) => {
    const searchText = elementsSearchText.value.toLowerCase();
    return index.title.toLowerCase().includes(searchText);
  });
});

const affectedItemTitle = computed(() => {
  const item = affectedItem.value;
  if (!item) return "";
  if (item.title) return item.title;
  if (item.type == "flow") return useFlowStore().titles[item.id];
  return t("labels.unnamed");
});

const expressionInputField = ref<InstanceType<typeof ExpressionInput>>();

watch(inputType, () => {
  nextTick(() => {
    expressionInputField.value?.updateSelectionRange();
  });
});

function insertVariable(code: string) {
  if (inputType.value == "template") {
    addTemplateArg("variable", code);
  } else {
    props.tree.insertVariable(code);
  }
}

function insertElement(uid: string) {
  if (inputType.value == "template") {
    addTemplateArg("item", uid);
  } else {
    props.tree.insertItem(uid);
  }
}

function insertFunction(code: FunctionName) {
  props.tree.insertFunction(code);
}

function insertOperator(operator: ExpressionOperator | "!" | "=") {
  props.tree.insertOperator(operator);
}

function insertParenthesis(parenthesis: "opening" | "closing") {
  props.tree.insertParenthesis(parenthesis);
}

function addTemplateArg(type: TemplateSingleArg["type"], value: string) {
  templateInput.value?.addTemplateArg(type, value);
}
function addTemplateGroupArg(value: TemplateGroupArg["value"]) {
  templateInput.value?.addTemplateGroupArg(value);
}

// ==============================
// OPENING / CLOSING

function onShow() {
  console.log("expression editor open");
  expressionInputField.value?.updateSelectionRange();
}
function onClose() {
  emit("close");
}
</script>

<style lang="scss">
.expression-editor {
  border-radius: 0.5rem;
  font-family: var(--font-family-ui);

  &__header {
    padding: 1rem;
    min-height: 3.5rem;
    background-color: var(--color-background);
  }
  &__headline {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr 7rem;
    align-items: center;
    padding-right: 4rem;
  }
  &__heading {
    font-size: 1.5rem;
  }

  &__expression {
    display: flex;
    padding: 1rem;
    align-items: center;
    gap: 0.5rem;
  }
  &__tree {
    padding: 1rem;
  }

  &__result {
    padding: 0.25rem 1rem;
    padding-left: 1.5rem;
    padding-top: 0;
    padding-bottom: 1rem;
    margin-top: -0.5rem;
  }
  &__result-value {
    color: var(--color-text-medium);
    font-size: 14px;
  }
  &__result-range {
    padding-left: 0.5rem;
    color: var(--color-text-light);
    font-size: 12px;
  }

  &__controls {
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: 1rem;
  }
  &__subtitle {
    padding-top: 0.5rem;
    margin-top: 0;
    white-space: nowrap;
    font-size: 1.125rem;
    font-weight: 500;
  }

  &__buttonsarea {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  &__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    max-height: 8rem;
    overflow: auto;
    padding-inline: 0.25rem;
    margin-left: -0.25rem;
    padding-bottom: 0.5rem;
    margin-bottom: -0.5rem;
  }
  &__operatorbutton {
    border-radius: 3rem;
  }

  &__prefix {
    display: inline-block;
    color: var(--color-text-light);
    font-weight: 400;
    width: 1.25rem;
    text-align: center;
  }

  &__template {
    padding-top: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    height: 22rem;
    display: grid;
    gap: 1rem;
    grid-template-columns: 3rem minmax(20rem, 30rem) auto;
    grid-template-rows: minmax(0, 1fr);
    padding-left: 1rem;
  }

  &__stepnumber {
    display: flex;
    flex-shrink: 0;
    padding: 1rem;
    border-radius: 50%;
    border: 1px solid var(--color-border);
    height: 3rem;
    width: 3rem;
    align-items: center;
    justify-content: center;
  }
}
</style>
